import type { JSX } from "solid-js";
import { Title } from "@solidjs/meta";
import { TextLinkA } from "~/common/text_link";
import { createPatientText, type PatientI18n, PatientTextProvider } from "~/patient_i18n/i18n";
import { SmsOptInForm } from "~/sms_opt_in/opt_in_form";
import { optInPaths, type OptInOwner } from "~/sms_opt_in/paths";
import { ContentRenderer } from "~/util/content_renderer";
import { LocationPanel, PublicPageShell } from "../_location_card";

type Props = {
  owner: OptInOwner;
  locationName: string;
  logoUrl: string | null;
  addressLines: string[] | null;
  phone: string | null;
  consentLabel: string;
  description: string;
  termsAndConditions: string;
  privacyPolicy: string;
  patientI18n: PatientI18n;
};

// For carrier reviewers, not patients: the server always renders it in English (the campaign's
// message flow is English), and the form preview reads that English through the patient strings.
export default function SmsOptInDetails(props: Props) {
  return (
    // eslint-disable-next-line solid/reactivity
    <PatientTextProvider value={createPatientText(props.patientI18n)}>
      <Title>SMS opt-in details - {props.locationName}</Title>

      <PublicPageShell maxWidth="4xl">
        <div class="flex flex-col gap-12">
          <LocationPanel {...props}>
            <h1 class="mb-4 text-2xl font-semibold">How patients opt in to text messages</h1>
            <ContentRenderer content={props.description} trustedContent />
          </LocationPanel>

          <Preview page={props} title="SMS opt-in form" href={optInPaths(props.owner).optIn}>
            <SmsOptInForm
              owner={props.owner}
              locationName={props.locationName}
              consentLabel={props.consentLabel}
            />
          </Preview>

          <Preview
            page={props}
            title="Terms & Conditions"
            href={optInPaths(props.owner).termsAndConditions}
          >
            <ContentRenderer content={props.termsAndConditions} trustedContent />
          </Preview>

          <Preview page={props} title="Privacy Policy" href={optInPaths(props.owner).privacyPolicy}>
            <ContentRenderer content={props.privacyPolicy} trustedContent />
          </Preview>
        </div>
      </PublicPageShell>
    </PatientTextProvider>
  );
}

/** A non-interactive render of a live public page, standing in for a screenshot. */
function Preview(props: { page: Props; title: string; href: string; children: JSX.Element }) {
  return (
    <section class="flex flex-col gap-3">
      <div class="flex items-baseline justify-between gap-4">
        <h2 class="text-lg font-semibold">{props.title}</h2>
        <TextLinkA href={props.href} destination="new-tab" class="text-sm text-brand-primary">
          Open live page
        </TextLinkA>
      </div>
      {/* A disabled fieldset, not `inert`: the controls can't submit, but screen readers still
          read the preview. */}
      <fieldset
        disabled
        class="min-w-0 rounded-3xl border border-dashed border-brand-subtle p-3 sm:p-4"
      >
        <LocationPanel {...props.page}>{props.children}</LocationPanel>
      </fieldset>
    </section>
  );
}
